import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import "./DonateImpact.css";
import { PiMountains } from "react-icons/pi";
import { BsAirplaneEngines } from "react-icons/bs";
import { GoPeople } from "react-icons/go";
import { PiPlant } from "react-icons/pi";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease, delay } },
});

/* ── Impact pillars ────────────────────────────────────────── */
const PILLARS = [
  {
    Icon: PiMountains,
    label: "Fund Retreats",
    desc: "Cover lodging, meals, facilitation, and integration for every participant.",
    accent: false,
  },
  {
    Icon: BsAirplaneEngines,
    label: "Support Travel",
    desc: "Help cover flights and transportation so cost is never a barrier to healing.",
    accent: true,
  },
  {
    Icon: GoPeople,
    label: "Build Community",
    desc: "Strengthen a lifelong brotherhood that continues long after the retreat ends.",
    accent: false,
  },
  {
    Icon: PiPlant,
    label: "Create Lasting Change",
    desc: "Invest in a model of healing that creates ripples for families and future generations.",
    accent: true,
  },
];

/* ── Dark strip stats ──────────────────────────────────────── */
const STATS = [
  { value: 1247, suffix: "+", label: "Lives Impacted", accent: true },
  { value: 32, suffix: "", label: "States Reached", accent: false },
  { value: 9, suffix: "", label: "Retreats Completed", accent: false },
  { value: 456, suffix: "+", label: "Volunteers & Supporters", accent: true },
];

function useCounter(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let ts = null;
    const step = (t) => {
      if (!ts) ts = t;
      const p = Math.min((t - ts) / duration, 1);
      setVal(Math.floor((1 - Math.pow(2, -10 * p)) * target));
      if (p < 1) requestAnimationFrame(step);
      else setVal(target);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return val;
}

function StatItem({ stat, active }) {
  const count = useCounter(stat.value, active);
  const display = stat.value >= 1000 ? count.toLocaleString() : String(count);
  return (
    <div className="dni__stat">
      <div className={`dni__stat-num ${stat.accent ? "dni__stat-num--rust" : ""}`}>
        {display}
        {stat.suffix}
      </div>
      <div className="dni__stat-label">{stat.label.toUpperCase()}</div>
      <span className={`dni__stat-rule ${stat.accent ? "dni__stat-rule--rust" : ""}`} aria-hidden="true" />
    </div>
  );
}

export default function DonateImpact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <div className="dni" ref={ref}>
      {/* ── Cream top: eyebrow + 4 pillars ── */}
      <div className="dni__top">
        <div className="container-fg container-2xxl">
          <motion.div className="dni__eyebrow-row" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <span className="dni__eyebrow-rule" aria-hidden="true" />
            <p className="dni__eyebrow u-eyebrow">Your Support Makes an Impact</p>
            <span className="dni__eyebrow-rule" aria-hidden="true" />
          </motion.div>

          <div className="dni__pillars">
            {PILLARS.map((p, i) => (
              <motion.div key={p.label} className="dni__pillar" variants={fadeUp(0.08 + i * 0.09)} initial="hidden" animate={inView ? "show" : "hidden"}>
                <div className={`dni__pillar-icon ${p.accent ? "dni__pillar-icon--rust" : ""}`} aria-hidden="true">
                  <p.Icon size={82} color={p.accent ? "var(--c-rust)" : "var(-c--ink)"} />
                </div>
                <h3 className={`dni__pillar-label  ${p.accent ? "dni__pillar-label--rust" : ""}`}>{p.label.toUpperCase()}</h3>
                <p className="dni__pillar-desc">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Dark mountain strip ── */}
      <div className="dni__strip">
        <div className="dni__strip-bg" aria-hidden="true">
          <img src="./assets/mountainsForst.png" alt="" />
        </div>
        <div className="container-fg container-2xxl dni__strip-inner">
          {STATS.map((s, i) => (
            <motion.div key={s.label} variants={fadeUp(0.05 + i * 0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
              <StatItem stat={s} active={inView} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
